// The 3D view's pointer (docs/claude/handles.md, *The 3D pointer*): what a
// press, a click, the hover, a double-click and the cursor take where the
// pointer is — asked ONCE per event, in ONE order, from what lies under it: the
// 3D twin of handleHits.ts. It was answered by five listeners in three files,
// each over its own subset of the widgets, and they disagreed: a double-click on
// the gizmo's ring moved the camera, the ring's hover outlined what lay under it,
// and an armed catalogue product lost its click to a handle, an arrow or the
// ring under it. The hit tests need the view and stay in ui/pointer3dUi.ts (one
// probe per kind); what lives here is DOM-free:
//  - `POINTER3D_ORDER`: every kind, in the order they are tried — the kind union
//    is read off it, so a new kind is placed in the order or is not one;
//  - `pointer3dOf`: the first probe that hits (the scene's always does);
//  - what each kind's press, click, hover, double-click and cursor do: Records
//    over the kind, so a new kind does not compile until each says.
import type {
  GizmoArrow,
  GizmoAxis,
} from './gizmo';
import type { ArmedPick } from './ui/armedPick';
import type { Handle3d } from './ui/handleWork';
import type { Tool3dRow } from './ui/tools3d';

/** Every kind of thing the 3D pointer can be on, in the order it is tried:
 *  where two apply, the first listed takes the press, the click, the hover, the
 *  double-click and the cursor. A work under way and an armed pick apply
 *  wherever the pointer is, the tool's row whenever one is armed. */
export const POINTER3D_ORDER = [
  'armedHandle',
  'armedPick',
  'handle',
  'arrow',
  'ring',
  'tool',
  'scene',
] as const;
export type Pointer3dKind = (typeof POINTER3D_ORDER)[number];

/** What a hit of each kind carries. */
export interface Pointer3dPayload {
  /** A handle's work under way in 3D (ui/handleWork.ts): a drag, or one a click
   *  armed (`armed`: it follows the pointer, the next press drops it). */
  armedHandle: { armed: boolean };
  /** A pick armed for the 3D view (ui/armedPick.ts, one with `click3d`: a
   *  catalogue product to place). */
  armedPick: { pick: ArmedPick };
  /** One of the selection's 3D handles. */
  handle: { handle: Handle3d };
  /** One of the move gizmo's arrows. */
  arrow: { arrow: GizmoArrow };
  /** The move gizmo's rotate ring. */
  ring: object;
  /** The armed tool's row (ui/tools3d.ts `TOOL_3D`). */
  tool: { row: Tool3dRow };
  /** What the view shows there: the camera's press, the scene pick's click. */
  scene: object;
}

/** What is under the pointer: a tagged union over every kind. */
export type Pointer3dHit = { [K in Pointer3dKind]: { kind: K } & Pointer3dPayload[K] }[Pointer3dKind];
/** A hit of one kind. */
export type Pointer3dHitOf<K extends Pointer3dKind> = Extract<Pointer3dHit, { kind: K }>;

/** One hit test per kind: its payload when it applies at the pointer, else null. */
export type Pointer3dProbes<A extends unknown[]> = { [K in Pointer3dKind]: (...args: A) => Pointer3dPayload[K] | null };

/** The first kind, in `POINTER3D_ORDER`, whose probe hits — later probes are not
 *  asked —; the scene when none does. */
export function pointer3dOf<A extends unknown[]>(probes: Pointer3dProbes<A>, ...args: A): Pointer3dHit {
  for (const kind of POINTER3D_ORDER) {
    const p = probes[kind](...args);
    if (p) return { ...p, kind } as Pointer3dHit;
  }
  return { kind: 'scene' };
}

/** The hit when it is of `kind`, else null. */
export function pointer3dHitOf<K extends Pointer3dKind>(hit: Pointer3dHit, kind: K): Pointer3dHitOf<K> | null {
  return hit.kind === kind ? (hit as Pointer3dHitOf<K>) : null;
}

/** What a press does:
 *  - `drop`: the armed handle's work ends where it is (any button);
 *  - `work`: the handle's, the arrow's or the ring's work begins — the press is
 *    the overlay's, the camera never sees it;
 *  - `own`: the tool's own press, or the scene's in Select (a drag tool's drag,
 *    a spot dragged), which may take it — else it is the view's;
 *  - `view`: the camera's (an orbit, a pan); a click without travel is then the
 *    release's (`CLICK_3D`).
 *  Only a left press does anything but a drop: the others are the camera's. */
export type Press3d = 'drop' | 'own' | 'view' | 'work';
export const PRESS_3D: { [K in Pointer3dKind]: (hit: Pointer3dHitOf<K>) => Press3d } = {
  armedHandle: (h) => (h.armed ? 'drop' : 'view'),
  armedPick: () => 'view',
  handle: () => 'work',
  arrow: () => 'work',
  ring: () => 'work',
  tool: () => 'own',
  scene: () => 'own',
};

/** What a press there does (`PRESS_3D`), `left` for the left button. */
export function pressOf(hit: Pointer3dHit, left: boolean): Press3d {
  const p = (PRESS_3D[hit.kind] as (h: Pointer3dHit) => Press3d)(hit);
  return p === 'drop' || left ? p : 'view';
}

/** What a left click the view was left does where it is released: `pick` the
 *  armed pick's click (`click3d`), `tool` the tool's click where it lands,
 *  `select` the scene pick (on nothing: deselect); `none` for what takes its
 *  own press (its click is that press's). */
export type Click3d = 'none' | 'pick' | 'select' | 'tool';
export const CLICK_3D: Record<Pointer3dKind, Click3d> = {
  armedHandle: 'none',
  armedPick: 'pick',
  handle: 'none',
  arrow: 'none',
  ring: 'none',
  tool: 'tool',
  scene: 'select',
};

/** What a click there does (`CLICK_3D`). */
export function clickOf(hit: Pointer3dHit): Click3d {
  return CLICK_3D[hit.kind];
}

/** What the hover shows where the pointer rests (no button held): */
export interface Hover3d {
  /** What a click would take is outlined (the scene pick's). */
  outline: boolean;
  /** The gizmo's arrow, or its ring (`turn`), lit. */
  lit: GizmoAxis | 'turn' | null;
  /** Whose preview follows the pointer: the armed pick's (`move3d`), the tool's
   *  (its row's: where a click lands, the area it adds, its own preview). */
  preview: 'pick' | 'tool' | null;
}
const HOVER_NONE: Hover3d = { outline: false, lit: null, preview: null };
export const HOVER_3D: { [K in Pointer3dKind]: (hit: Pointer3dHitOf<K>) => Hover3d } = {
  armedHandle: () => HOVER_NONE,
  // No outline promises a click the pick takes.
  armedPick: () => ({ outline: false, lit: null, preview: 'pick' }),
  handle: () => HOVER_NONE,
  arrow: (h) => ({ outline: false, lit: h.arrow.axis, preview: null }),
  ring: () => ({ outline: false, lit: 'turn', preview: null }),
  // A tool with its own preview (the 3D section's plane) shows that instead.
  tool: (h) => ({ outline: !h.row.own, lit: null, preview: 'tool' }),
  scene: () => ({ outline: true, lit: null, preview: null }),
};

/** What the hover shows there (`HOVER_3D`). */
export function hoverOf(hit: Pointer3dHit): Hover3d {
  return (HOVER_3D[hit.kind] as (h: Pointer3dHit) => Hover3d)(hit);
}

/** Whether a double-click there moves the camera to orbit the point clicked:
 *  not on what takes its own presses (a work under way, a handle, an arrow, the
 *  ring — two clicks there arm and drop it). */
export const FOCUS_3D: Record<Pointer3dKind, boolean> = {
  armedHandle: false,
  armedPick: true,
  handle: false,
  arrow: false,
  ring: false,
  tool: true,
  scene: true,
};

/** Whether a double-click there focuses the camera (`FOCUS_3D`). */
export function focusOf(hit: Pointer3dHit): boolean {
  return FOCUS_3D[hit.kind];
}

/** The pointer each kind shows: move over what a press drags, the armed pick's
 *  own (a product's copy cursor), the tool's row's (the 3D section's crosshair). */
export const CURSOR_3D: { [K in Pointer3dKind]: (hit: Pointer3dHitOf<K>) => string } = {
  armedHandle: () => 'move',
  armedPick: (h) => h.pick.cursor3d ?? '',
  handle: () => 'move',
  arrow: () => 'move',
  ring: () => 'move',
  tool: (h) => h.row.cursor ?? '',
  scene: () => '',
};

/** The pointer shown there (`CURSOR_3D`). */
export function cursor3dOf(hit: Pointer3dHit): string {
  return (CURSOR_3D[hit.kind] as (h: Pointer3dHit) => string)(hit);
}
